

import isEqual from 'lodash/isEqual';
import { merge } from 'lodash'
import eventBus from '@/utils/event-bus';
import { getPermanentData } from '@/utils/browser-storage';
import { getPoiBySlug, checkIsSubApi, getParentApi } from '@/utils/poi-utils'
import { getPoiConfig } from '@/utils/poi-data/poi-config'
import { formatDate } from '@/utils/format'
import transportModes from '@/data/transportModes'
import router from '@/router';
import store from '@/store'
import { ROUTE_OPTION_QUERY_PARAMS, encodeRouteOptions, decodeRouteOptions, getRouteBaseQueryOptions } from '@/utils/url/route-options';
import { POI_OPTION_QUERY_PARAMS, encodePoiOptions, getPoiBaseQueryOptions, decodePoiOptions } from '@/utils/url/poi-options';
import { CUSTOM_LOCATION_TYPES } from '@/data/locationTypes'

const ALLOWED_QUERIES_FOR_ROUTES = {
    'Main': ['lat', 'long', 'zoom', 'datum', 'zeit', 'modus'],
    'POI Category': ['lat', 'long', 'zoom', 'datum', 'zeit', ...POI_OPTION_QUERY_PARAMS],
    'POI Detail': ['lat', 'long', 'zoom', 'sub', 'preset', 'datum', 'zeit'],
    'Route': ['lat', 'long', 'zoom', 'verkehrsmittel', 'start', 'ziel', 'via', 'datum', 'zeit', 'modus', 'pois', ...ROUTE_OPTION_QUERY_PARAMS],
}

export function checkUrl(route) {
    checkUrlQuery(route)
    if(route.name === 'POI Category' && route.params.poicategory) {
        const poiValues = getPoiValuesFromUrl(route)
        store.commit('setCategoriesFromUrl', poiValues)
        setTimeout(() => {
            eventBus.$emit('activatePOICategory', poiValues)
        }, 10);
    } else if(route.name === 'POI Detail' && route.params.poicategory && route.params.poiid) {
        const data = getPOIDetailFromUrl(route)
        setTimeout(() => {
            eventBus.$emit('showPOIDetail', data)
        }, 10);
    } else if(route.name === 'Route') {
        setTimeout(() => {
            eventBus.$emit('showWaypointsFromUrl')
        }, 10);
    }
}

export function checkUrlQuery(route) {
    const queryData = decodeQueryParams(route.query, route)
    if(queryData.center) {
        const centerFromUrl = {
            center: queryData.center,
            zoom: queryData.zoom
        }
        store.commit('setCenterFromUrl', centerFromUrl)
    }
    if(queryData.start || queryData.goal || queryData.via) {
        const waypoints = getWaypointsAsList(queryData)
        store.commit('setWaypointsFromUrl', waypoints)
    }
    if(queryData.transportMode) {
        store.commit('setTransportModeFromUrl', queryData.transportMode)
    }
    // route time
    let routeTime = false
    if(queryData.date && queryData.time) {
        if(!routeTime) routeTime = {}
        const splitDate = queryData.date.split('.')
        const parsedDate = new Date(splitDate[2], splitDate[1]-1, splitDate[0])
        routeTime = {
            isNow: false,
            date: parsedDate,
            time: queryData.time,
        }
    }
    if(queryData.mode) {
        if(!routeTime) routeTime = { isNow: true } // no route time yet from URL so we can assume it should be now
        routeTime.mode = queryData.mode
    }
    if(routeTime) {
        store.commit('setRouteTrafficTime', routeTime)
    }
    // options
    if(queryData.routeOptions) {
        const queryRouteOptions = JSON.parse(JSON.stringify(queryData.routeOptions))
        if(Object.keys(queryRouteOptions).length > 0) {
            const defaultRouteOptions = JSON.parse(JSON.stringify(store.getters.routeSettings))
            console.log('DEFAULT route options', defaultRouteOptions)
            const mergedOptions = merge(defaultRouteOptions, queryRouteOptions) // merge with the user saved route options to avoid issues when switching transport modes
            store.commit('setQueryRouteSettings', mergedOptions)
            store.commit('setQueryRouteSettingsChanges', mergedOptions)
        }
    }
    if(queryData.poiOptions) {
        const storedPoiOptions = getPermanentData('poi-settings')
        const poiOptions = queryData.poiOptions
        let mergedPoiOptions = {...poiOptions}
        if (storedPoiOptions) {
            mergedPoiOptions = {...storedPoiOptions, ...poiOptions}
        }
        if(Object.keys(mergedPoiOptions).length) {
            store.commit('setQueryPoiSettings', mergedPoiOptions)
        }
    }

    if(queryData.specialContent) {
        store.commit('setSpecialContentFromUrl', queryData.specialContent)
    }

    if(queryData.pois) {
        store.commit('setCategoriesFromUrl', queryData.pois)
        setTimeout(() => {
            eventBus.$emit('activatePOICategory', queryData.pois)
        }, 10);
    }
}

// POI
export function getPOIDetailFromUrl(route) {
    const poi = getPoiBySlug(route.params.poicategory)
    let subType = false
    if(route.query.sub && poi.categories) {
        for(const c of poi.categories) {
            if(c.slug === route.query.sub) {
                subType = c.apiType
                break;
            }
        }
    }
    if(route.query.sub === 'prediction') subType = 'prediction'

    return {
        type: poi.value,
        subType: subType,
        id: route.params.poiid
    }
}

export function updatePOICategoryUrl(route, values) {
    const poiCategories = getPoiSlugs(values)
    const poicategoryForUrl = poiCategories.join(',')

    if(route.name === 'POI Category' && route.params.poicategory) {
        if(route.params.poicategory === poicategoryForUrl) return false
    }

    if(route.name === 'Route') {
        const queryData = {
            pois: poicategoryForUrl
        }
        const newQueryParams = getUpdatedQueryParams(route, queryData, 'Route')
        const allowedQueryParams = checkForAllowedQueryParams('Route', newQueryParams)
        console.log(newQueryParams)
        router.push({
            name: 'Route',
            query: allowedQueryParams
        })
        return false
    }

    const allowedQueryParams = checkForAllowedQueryParams('POI Category', route.query)
    router.push({
        name: 'POI Category',
        params: { 
            poicategory: poicategoryForUrl
        },
        query: allowedQueryParams
    })
}

function getPoiSlugs(values) {
    const poiCategories = []
    for(const value of values) {
        const poiConfig = getPoiConfig(value)
        if(poiConfig) poiCategories.push(poiConfig.slug)
    }
    return poiCategories
}

export function updatePOIDetailUrl(route, poiCategory, poiSubCategory, poiId) {
    // if user clicks on poi category while route is active, do not redirect to poi category 
    if(route.name === 'Route') {
        const newRouteQuery = { ...route.query }
        newRouteQuery.poidetail = poiId
        router.push({
            name: 'Route',
            query: newRouteQuery
        })
        return false
    }
    if(!poiId) {
        if(store.getters.selectedPOIs.length === 0) {
            const allowedQueryParams = checkForAllowedQueryParams('Main', route.query)
            router.push({
                name: 'Main',
                query: allowedQueryParams
            })
        } else {
            updatePOICategoryUrl(route, store.getters.selectedPOIs)
        }
        return
    }
    const isSubApi = checkIsSubApi(poiCategory)
    const poiType = isSubApi ? getParentApi(poiCategory) : poiCategory
    const poiConfig = getPoiConfig(poiType)
    if(poiConfig && poiId !== route.params.poiid) {
        const slug = poiConfig.slug
        const params = { 
            poicategory: slug,
            poiid: poiId
        }
        const query = {...route.query}
        if(poiSubCategory) {
            if(poiConfig.categories) {
                for (const c of poiConfig.categories) {
                    if(c.value === poiSubCategory || c.apiType === poiSubCategory) {
                        query.sub = c.slug
                        break;
                    }
                }
            }
        }
        if(poiSubCategory === 'prediction') query.sub = 'prediction'

        const allowedQueryParams = checkForAllowedQueryParams('POI Detail', query)
        router.push({
            name: 'POI Detail',
            params,
            query: allowedQueryParams
        })
    }
}

function getPoiValuesFromUrl(route) {
    if(route.name === 'POI Category' && route.params.poicategory) {
        const poiCategories = route.params.poicategory.split(',')
        const pois = poiCategories.map(category => getPoiBySlug(category))
        const poiValues = pois.map(poi => poi.value)
        return poiValues
    } else if(route.name === 'Route' && route.query.pois) {
        const poiCategories = route.query.pois.split(',')
        const pois = poiCategories.map(category => getPoiBySlug(category))
        const poiValues = pois.map(poi => poi.value)
        return poiValues
    }
    return false
}

// ROUTE
export function updateRouteUrl(route, data) {
    const waypoints = data.waypoints
    let queryData = {}
    // route time
    if(data.routeTrafficTime) {
        const routeTime = data.routeTrafficTime
        if(routeTime.isNow) {
            queryData.date = ''
            queryData.time = ''
            queryData.mode = routeTime.mode ?? 'departure'
        } else {
           queryData = {...queryData, ...routeTime}
        }
    }

    if(data.pois) {
        const poiCategories = getPoiSlugs(data.pois)
        const poicategoryForUrl = poiCategories.join(',')
        queryData.pois = poicategoryForUrl
    }

    if(waypoints && waypoints.length > 0 && (waypoints[0].name || waypoints[waypoints.length - 1].name)) {
        if(waypoints[0].name) {
            queryData.start = waypointToJSONString(waypoints[0])
        }
        if(waypoints[waypoints.length - 1].name) {
            queryData.goal = waypointToJSONString(waypoints[waypoints.length - 1])
        }
        if(waypoints.length > 2) {
            const interimWaypoints = waypoints.slice(1, waypoints.length - 1)
            queryData.via = interimWaypoints.map(waypoint => `${waypointToJSONString(waypoint)}`).join(';')
        } else {
            queryData.via = ''
        }
        const newQueryParams = getUpdatedQueryParams(route, queryData, 'Route')
        if(queryParamsDifferent(route.query, newQueryParams)) {
            router.push({
                name: 'Route',
                params: route.params,
                query: newQueryParams
            })
        }
    } else {
        const baseData = {
            start: '',
            goal: '',
            via: '',
            transportMode: '',
        }
        if(queryData.date !== undefined) baseData.date = queryData.date
        if(queryData.time !== undefined) baseData.time = queryData.time
        if(queryData.mode !== undefined) baseData.mode = queryData.mode
        queryData = {...baseData}

        const poisSelected = store.getters.selectedPOIs.length > 0
        const upcomingRoute = poisSelected ? 'POI Category' : 'Main'
        const newQueryParams = getUpdatedQueryParams(route, queryData, upcomingRoute)

        if(poisSelected) {
            const poiCategories = getPoiSlugs(store.getters.selectedPOIs)
            const poicategoryForUrl = poiCategories.join(',')
            router.push({
                name: 'POI Category',
                params: { 
                    poicategory: poicategoryForUrl
                },
                query: newQueryParams
            })
        } else {
            if(queryParamsDifferent(route.query, newQueryParams)) {
                router.push({
                    name: upcomingRoute,
                    params: route.params,
                    query: newQueryParams
                })
            }
        }
    }
}

export function queryWaypointsDifferent(oldQuery, newQuery) {
    const startDifferent = newQuery.start ? newQuery.start !== oldQuery.start : (oldQuery.start ? true : false)
    const goalDifferent = newQuery.ziel ? newQuery.ziel !== oldQuery.ziel : (oldQuery.ziel ? true : false)
    const viaDifferent = newQuery.via ? newQuery.via !== oldQuery.via : (oldQuery.via ? true : false)
    return (startDifferent || goalDifferent || viaDifferent)
}

export function queryParamsDifferent(oldQuery, newQuery) {
    return !isEqual(oldQuery, newQuery)
}

export function getWaypointsAsList(queryData) {
    let waypoints = []
    if(queryData.start) {
        waypoints.push(JSON.parse(queryData.start))
    } else {
        waypoints.push({})
    }
    if(queryData.via) {
        let viaWaypoints = queryData.via.split(';')
        viaWaypoints = viaWaypoints.filter(waypoint => waypoint !== '')
        if(viaWaypoints.length > 0) {
            const formattedViaWaypoints = []
            viaWaypoints.forEach(waypoint => { 
                formattedViaWaypoints.push(JSON.parse(waypoint))
            })
            waypoints = [...waypoints, ...formattedViaWaypoints]
        }
    }
    if(queryData.goal) {
        waypoints.push(JSON.parse(queryData.goal))
    } else {
        waypoints.push({})
    }
    return waypoints
}

// QUERY PARAMS
export function getUpdatedQueryParams(route, data, nextRouteName = false) {
    const queryParams = encodeQueryParams(route, data)
    const currentQueryParams = route.query
    let mergedQueryParams = {...currentQueryParams, ...queryParams}
    const routeName = nextRouteName ?? route.name
    mergedQueryParams = checkForAllowedQueryParams(routeName, mergedQueryParams)
    return mergedQueryParams
}

export function checkForAllowedQueryParams(routeName, data) {
    const query = {...data}
    const allowedQueryParams = ALLOWED_QUERIES_FOR_ROUTES[routeName]
    for(const key in query) {
        if(query[key] === '' || (allowedQueryParams && !allowedQueryParams.includes(key))) {
            delete query[key]
        }
    }
    return query
}

export function updateQueryParams(route, data) {
    const newQueryParams = getUpdatedQueryParams(route, data)
    if(queryParamsDifferent(route.query, newQueryParams)) {
        router.push({...route, ...{ query: newQueryParams }})
    }
}

export function encodeQueryParams(route, data) {
    let query = {...route.query}
    if(data.search !== undefined) {
        query.suche = data.search
    }
    if(data.start !== undefined) {
        query.start = data.start
    }
    if(data.goal !== undefined) {
        query.ziel = data.goal
    }
    if(data.via !== undefined) {
        query.via = data.via
    }
    if(data.transportMode !== undefined) {
        const transportModeConfig = transportModes.find(mode => mode.value === data.transportMode)
        if(transportModeConfig) query.verkehrsmittel = transportModeConfig.urlLabel
    }
    if(data.center !== undefined) {
        query.lat = data.center.lat
        query.long = data.center.lng
        if(data.zoom !== undefined) {
            const zoomRounded = Math.round(data.zoom * 10) / 10
            query.zoom = zoomRounded
        }
    }
    if(data.date !== undefined) {
        query.datum = data.date !== '' ? formatDate(data.date) : ''
    }
    if(data.time !== undefined) {
        query.zeit = data.time
    }
    if(data.mode !== undefined) {
        query.modus = data.mode === 'arrival' ? 'ankunft' : ''
    }
    if(data.pois !== undefined) {
        query.pois = data.pois
    }
    if(data.poiOptions !== undefined) {

        const fuelSettingsChanged = data.poiOptions.gasStations && data.poiOptions.gasStations.fuels && data.poiOptions.gasStations.fuels[0] !== 'DIESEL' // special because this is not reflected in the poiSettingsChanged

        if(route.name === 'POI Category' && (store.getters.poiSettingsChanged.length > 0 || fuelSettingsChanged)) {
            const poiCategories = []
            store.getters.selectedPOIs.forEach(poi => {
                if(store.getters.poiSettingsChanged.includes(poi)) {
                    poiCategories.push(poi)
                }
            })
            if(fuelSettingsChanged && !poiCategories.includes('gasStations')) poiCategories.push('gasStations')
            const encodedOptions = encodePoiOptions(data.poiOptions, poiCategories)
            query = {...query, ...encodedOptions}
        } else if(route.name === 'POI Category') {
            query = {...query, ...getPoiBaseQueryOptions()}
        }
        if(data.poiOptions === false) {
            query.treibstoff = ''
        }
    }

    if(data.routeOptions !== undefined) {
        const activeTransportMode = store.getters.activeTransportModeTab
        if(route.name === 'Route' && store.getters.routeSettingsChanges.includes(activeTransportMode)) {
            const activeTransportModeOptions = data.routeOptions[activeTransportMode.toLowerCase()]
            const encodedOptions = encodeRouteOptions(activeTransportModeOptions)
            query = {...query, ...encodedOptions}
        } else if(route.name === 'Route') { // if the route settings are returned to the default settings, remove the params from the url
            query = {...query, ...getRouteBaseQueryOptions()}
        }
    }
    return query
}

export function decodeQueryParams(query, route = false) {
    const data = {
        poiOptions: {},
        routeOptions: {},
    }
    // map view related
    if(query.lat && query.long) {
        data.center = {
            lat: parseFloat(query.lat),
            lng: parseFloat(query.long)
        }
    }
    if(query.zoom) {
        data.zoom = parseFloat(query.zoom)
    }
    // route related
    if(query.start) {
        data.start = query.start
    }
    if(query.ziel) {
        data.goal = query.ziel
    }
    if(query.via) {
        data.via = query.via
    }
    if(query.verkehrsmittel) {
        const transportModeConfig = transportModes.find(mode => mode.urlLabel === query.verkehrsmittel)
        if(transportModeConfig) data.transportMode = transportModeConfig.value
    }
    if(query.datum) {
        data.date = query.datum
    }
    if(query.zeit) {
        data.time = query.zeit
    }
    if(query.modus) {
        data.mode = query.modus === 'ankunft' ? 'arrival' : ''
    }
    // settings related
    const poiSettings = JSON.parse(JSON.stringify(store.getters.poiSettings))
    if(query.typ) {
        if(route && route.name === 'POI Category') {
            const subCategories = query.typ.split(',')
            const poiValues = getPoiValuesFromUrl(route)
            poiValues.forEach(value => {
                if(!data.poiOptions[value]) data.poiOptions[value] = poiSettings[value]
                data.poiOptions[value].categories = subCategories
                if(value === 'traffic') {
                    data.poiOptions[value].type = subCategories  
                }
            })
        }
    }

    // poi options
    const queryPoiOptions = decodePoiOptions(query)
    if(Object.keys(queryPoiOptions).length > 0) {
        data.poiOptions = queryPoiOptions
    }

    // route options
    const transportMode = data.transportMode ? data.transportMode.toLowerCase() : 'car'
    let queryRouteOptions = decodeRouteOptions(query, transportMode)

    if(Object.keys(queryRouteOptions).length > 0) {
        data.routeOptions[transportMode] = queryRouteOptions
    }

    if(query.poipreset) {
        // only for traffic & camera for now
        if(query.poipreset === 'camera') {
            if(!data.poiOptions.traffic) data.poiOptions.traffic = poiSettings.traffic
            data.poiOptions.traffic.type = ['CAMERA']
        }
    }
    if(query.specialcontent) {
        if(query.specialcontent === 'tanken') {
            data.specialContent = 'fuelPrices'
        }
    }

    if(query.pois && route) {
        const poiValues = getPoiValuesFromUrl(route)
        data.pois = poiValues
    }
    return data
}

function waypointToJSONString(waypoint) {
     console.log(waypoint)
    const waypointData = {
        name: waypoint.name,
        lat: waypoint.lat,
        lon: waypoint.lon,
    }
    if(waypoint.id) {
        waypointData.id = waypoint.id
    }
    if(waypoint.comment) {
        waypointData.comment = waypoint.comment
    }
    if(waypoint.type) {
        waypointData.type = waypoint.type && !CUSTOM_LOCATION_TYPES.includes(waypoint.type) ? waypoint.type : 'GEO_ADDRESS'
    } else {
        waypointData.type = 'GEO_ADDRESS'
    }
    return JSON.stringify(waypointData)
}